import React, { useMemo } from 'react';
import MissionCardBody from '../shared/CardComponents/MissionCardBody';
import { getPrimaryTag } from '../hooks/useTagActions';
import { DEFAULT_WIDTH, MIN_HEIGHT } from '../FlowchartView/TaskNode/hooks/useTaskNodeStyles';
import '../FlowchartView/styles/node/index.css';

/**
 * The real mission card, used both as the picture source and as the card you edit.
 */
export default function CardFace({
  task,
  flow = {},
  lockState = null,
  selected = false,
  capture = false,
  onGoToDependency,
}) {
  const tags = flow.allTags?.length ? flow.allTags : (flow.allCategories || []);
  const category = useMemo(() => {
    const name = getPrimaryTag(task);
    if (!name) return null;
    return tags.find((item) => item.name === name) || { name, color: '#8b8ba3' };
  }, [task, tags]);
  const nestedSprint = (flow.nestedSprints || []).find((sprint) => sprint.id === task.nestedSprintId) || null;
  const completed = task.status === 'completed' || (nestedSprint && nestedSprint.progress === 100);

  const isDecision = task.nodeType === 'decision';
  const classes = [
    isDecision ? 'decision-node' : 'task-node',
    'board-card',
    selected && (isDecision ? 'decision-node--selected' : 'task-node--selected'),
    category && 'task-node--has-category',
    completed && 'task-node--completed',
    lockState?.isLocked && task.status !== 'completed' && 'task-node--locked',
    lockState?.isExcluded && 'task-node--excluded',
    task.status === 'blocked' && 'task-node--blocked',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      data-task-id={capture ? undefined : task.id}
      style={{
        position: 'relative',
        left: 'auto',
        top: 'auto',
        transform: 'none',
        width: task.width || (isDecision ? 288 : DEFAULT_WIDTH),
        minHeight: task.height || (isDecision ? 140 : MIN_HEIGHT),
        '--category-color': category?.color || 'transparent',
        '--category-bg': category ? `${category.color}18` : 'transparent',
        '--category-border': category ? `${category.color}35` : 'var(--border-color)',
      }}
    >
      <MissionCardBody
        task={task}
        category={category}
        allTags={tags}
        allTasks={flow.tasks || []}
        statuses={flow.statuses || []}
        nestedSprint={nestedSprint}
        nestedSprints={flow.nestedSprints || []}
        allSprints={flow.allSprints || []}
        isBlocked={task.status === 'blocked'}
        isLocked={!!lockState?.isLocked && task.status !== 'completed'}
        isExcluded={!!lockState?.isExcluded}
        lockReason={lockState?.lockReason || ''}
        blockingDependencies={lockState?.blockingTasks || []}
        parentMissionName={lockState?.parentMissionName}
        parentCampaignName={lockState?.parentCampaignName}
        parentMissionId={lockState?.parentMissionId}
        parentSprintId={lockState?.parentSprintId}
        onNavigateToParentMission={flow.onNavigateToParentMission}
        cardDisplaySettings={flow.cardDisplaySettings || {}}
        isTemplate={!!flow.isTemplate}
        campaignId={flow.campaignId}
        onRename={flow.onRenameTask}
        onIconChange={flow.onIconChange}
        onStatusChange={flow.onStatusChange}
        onAddTag={flow.onAddTag || flow.onCategoryChange}
        onRemoveTag={flow.onRemoveTag}
        onSetPrimaryTag={flow.onSetPrimaryTag}
        onRenameTag={flow.onRenameTag || flow.onRenameCategory}
        onDeleteTag={flow.onDeleteTag || flow.onDeleteCategory}
        onOpenDetails={flow.onOpenDetails}
        onOpenAttachments={flow.onOpenAttachments}
        onOpenNestedSprint={flow.onOpenNestedSprint}
        onRemoveNestedSprint={flow.onRemoveNestedSprint}
        onAddStatus={flow.onAddStatus}
        onDeleteStatus={flow.onDeleteStatus}
        onRenameStatus={flow.onRenameStatus}
        onSetStatusAutoSync={flow.onSetStatusAutoSync}
        onDueDateChange={flow.onDueDateChange}
        onRemindersChange={flow.onRemindersChange}
        onEstimatedDurationMinutesChange={flow.onEstimatedDurationMinutesChange}
        onCreatedAtChange={flow.onCreatedAtChange}
        onStartedAtChange={flow.onStartedAtChange}
        onCompletedAtChange={flow.onCompletedAtChange}
        onUpdatedAtChange={flow.onUpdatedAtChange}
        onPriorityChange={flow.onPriorityChange}
        onDifficultyChange={flow.onDifficultyChange}
        onCustomFieldValueChange={flow.onCustomFieldValueChange}
        customFields={flow.customFields || []}
        onArchiveTask={flow.onArchiveTask}
        onUnarchiveTask={flow.onUnarchiveTask}
        onDeleteTask={flow.onDeleteTask}
        onConditionsChange={flow.onConditionsChange}
        onAnswerChange={flow.onDecisionAnswerChange}
        onOptionsChange={flow.onDecisionOptionsChange}
        onGoToDependency={onGoToDependency}
        onSubmitDeliverable={flow.onSubmitDeliverable}
        onEditDeliverables={flow.onEditDeliverables}
        onClearDeliverable={flow.onClearDeliverable}
        currentView={flow.currentView}
        customViews={flow.customViews || []}
        activeCustomViewId={flow.activeCustomViewId}
      />
    </div>
  );
}
